import { Fragment, useEffect } from 'react'
import { classes, media, style } from 'typestyle'
import moment from 'moment'
import 'moment/min/locales'

/* Hooks ======================================================================================== */
import useFetch from '../../hooks/use-fetch'

/* Common ======================================================================================= */
import { flat, structureArray } from '../../share/obj-equal'
import { FieldObject } from '../../core/field-manager'

/* Utils ======================================================================================== */
import { formatPrice } from '../../share/format'

/* Modules ====================================================================================== */
import { formatDate } from '../../modules/fields/date-field'

/* Components =================================================================================== */
import { Box } from '../../components/box/box.component'
import { Button } from '../../components/button'
import { Line } from '../../components/line'
import { Modal } from '../../components/modal'
import { OperatorWrapper } from '../../core/operators/operator-wrapper'
import { PermissionFieldView } from '../fields/permission'
import { PermissionsFieldView } from '../fields/permissions'
import { RichTextView } from '../../components/field-editor-field/view/richt-text-view'
import { Switch } from '../components/switch'
import { Text } from '../../components/text/text.component'
import { UploadFieldView } from '../../components/upload-field/index.view'
import { HiddenFields } from '../../default/snippets/row-view'
import { withRouter } from '../../contexts/with-router'
import { DashboardPermissionFieldView } from '../fields/dashboard-permissions'

/* Constants ==================================================================================== */
import config from '../../../config'
import { get } from '../../share/find'
import { LOCAL_TYPE } from '../../hooks/use-local'
import { FIELDS } from '../../share/fields'
import { GLOBAL_CONTENTS } from '../../common/global-contents'

/* Styles ======================================================================================= */
import { createCustomMediaQuery } from '../../components/style-class/style-class.utils'
import { fitWidth } from '../../styles/styleset/fit-width/fitWidth'
import { mb16 } from '../../styles/styleset/margin/mb16'
import { mb32 } from '../../styles/styleset/margin/mb32'
import { mb4 } from '../../styles/styleset/margin/mb4'
import { mb8 } from '../../styles/styleset/margin/mb8'
import { ml8 } from '../../styles/styleset/margin/ml8'
import { mt8 } from '../../styles/styleset/margin/mt8'
import { p16 } from '../../styles/styleset/padding/p16'
import { pb4 } from '../../styles/styleset/padding/pb4'
import { pl12 } from '../../styles/styleset/padding/pl12'
import { pl8 } from '../../styles/styleset/padding/pl8'
import { pr12 } from '../../styles/styleset/padding/pr12'
import { pt8 } from '../../styles/styleset/padding/pt8'
import { w100 } from '../../styles/styleset/width/w100'
import { pr0i } from '../../styles/styleset/padding/pr0i'
import { pr4 } from '../../styles/styleset/padding/pr4'
import { pl4 } from '../../styles/styleset/padding/pl4'
import { pt4 } from '../../styles/styleset/padding/pt4'
import { f16i } from '../../styles/styleset/font-size/f16i'

const renderViewItem = (props: {
  action?: any
  alt?: any
  comments?: any[]
  component?: any
  dialogType?: string
  disablePadding?: boolean
  field: any
  flatten: any
  highlighted?: any
  isEditable?: boolean
  key: any
  local?: LOCAL_TYPE
  onEdit: any
  onHide: any
  onHighlight?: any
  onUpdate: any
  parsedValue?: any
  pathName: any
  readable?: boolean
  value?: any
}) => {
  const {
    alt,
    comments = [],
    component,
    dialogType,
    disablePadding,
    field,
    flatten,
    highlighted,
    isEditable,
    key,
    local = 'en',
    onEdit,
    onHide,
    onHighlight,
    onUpdate,
    parsedValue,
    pathName,
    readable = true,
    value,
  } = props

  const renderEditLabel = () => {
    switch (field.type) {
      case FIELDS.PASSWORD_CHANGE:
        return GLOBAL_CONTENTS.resetPassword[local]
      default:
        return GLOBAL_CONTENTS.edit[local]
    }
  }

  const renderButton = () => {
    switch (field.type) {
      case FIELDS.CHECKBOX:
        return <Switch value={value} pathName={pathName} onUpdate={onUpdate} name={field.name} />

      case FIELDS.GROUP:
        return <></>

      default:
        return (
          <>
            {onHide && (
              <Button
                icon={<i className={classes('material-icons-two-tone', f16i)}>visibility_off</i>}
                size="xs"
                baseline="none"
                onClick={() => {
                  onHide({
                    name: field.name,
                    parentLabel: field?.parentField?.label,
                    parentName: field?.parentField?.name,
                    label: field.label,
                  })
                }}
              />
            )}
            {onHighlight && (
              <Button
                icon={
                  <i className={classes('material-icons-two-tone', f16i)}>
                    {highlighted ? 'lightbulb_outline' : 'lightbulb'}
                  </i>
                }
                size="xs"
                baseline="none"
                onClick={() => {
                  onHighlight(field.name)
                }}
              />
            )}
            <Box baseline="none" relative>
              {/* <Button
                icon={<i className={classes('material-icons-two-tone', f16i)}>comment</i>}
                size="xs"
                baseline="none"
                // onClick={() => {
                //   onHighlight(field.name)
                // }}
              /> */}
              {comments.length > 0 && (
                <Box
                  absolute
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '100%',
                    right: -2,
                    top: -2,
                    cursor: 'pointer',
                  }}
                  flex
                  alignCenterX
                  alignCenterY
                  shadow
                >
                  <Text size="xs" shrink>
                    {comments.length}
                  </Text>
                </Box>
              )}
            </Box>
            {props.action}
            <Button
              className={classes(fitWidth, ml8)}
              label={renderEditLabel()}
              disabled={field.disabled}
              onClick={onEdit({ field, value, dialogType })}
              baseline={field.disabled ? 'disabled' : 'primary'}
              size="xs"
              square
              center
            />
          </>
        )
    }
  }

  return (
    <OperatorWrapper key={key} values={flatten} field={field}>
      {(params: any) => {
        const { disabled, hidden } = params

        if (hidden) {
          return <></>
        }

        if (alt) {
          return (
            <tr
              className={classes(style({ borderBottom: '1px solid rgb(220,220,220)', height: 32 }))}
            >
              <td>
                <Text size="sm">{field.label}</Text>
              </td>
              <td className={classes(pl8)}>
                {component}
                {!component && (
                  <Text size="sm">{parsedValue || (typeof value === 'string' ? value : '-')}</Text>
                )}
              </td>
            </tr>
          )
        }

        if (field.type === FIELDS.GROUP) {
          return (
            <Box
              className={classes(
                mb32,
                style({
                  pageBreakInside: 'avoid',
                  ...(field.style || {}),
                }),
              )}
              fullWidth
            >
              <Box
                spaceBetween
                alignCenterY
                className={classes(
                  readable && '',
                  style({
                    pageBreakInside: 'avoid',
                  }),
                )}
              >
                <Text baseline="surface" size="sm" semiBold>
                  {field.label}
                </Text>
                {renderButton()}
              </Box>
              <Box
                className={classes(
                  mt8,
                  mb8,
                  style({
                    pageBreakInside: 'avoid',
                  }),
                )}
                row
              >
                <Line dark />
              </Box>

              {component}
              {!component && (
                <Text size="sm" readable preLine>
                  {parsedValue || (typeof value === 'string' ? value : '-')}
                </Text>
              )}
            </Box>
          )
        }

        return (
          <Box
            className={classes(
              mb16,
              style({
                pageBreakInside: 'avoid',
              }),
            )}
          >
            <Box spaceBetween className={classes()} alignCenterY>
              <Box className={classes(readable && '', pr12)} row>
                <Text size="sm" baseline="surface" className={mb4} shrink>
                  {field.label}
                </Text>
              </Box>
            </Box>
            <Box
              baseline="background"
              className={classes(
                !disablePadding && pl12,
                style({
                  paddingTop: 4,
                  paddingBottom: field.type === FIELDS.REPEATER ? 0 : 4,
                  background: highlighted ? '#fff0c7' : '',
                }),
              )}
              minHeight={38}
              row={
                field.type === FIELDS.REPEATER ||
                (field.type === FIELDS.RICH_TEXT && value !== '<p><br></p>' && value !== '<p></p>')
              }
              spaceBetween
              alignCenterY
              rounded
            >
              <Box baseline="none" fullWidth>
                {component}
                {!component && (
                  <Text size="sm" readable preLine baseline="surface">
                    {parsedValue ||
                      (typeof value === 'string' || typeof value === 'number' ? value : '-')}
                  </Text>
                )}
              </Box>
              {/* {field.type === FIELDS.RICH_TEXT && <Line dark />} */}
              <Box
                className={classes(
                  field.type === FIELDS.REPEATER && pr0i,
                  field.type === FIELDS.RICH_TEXT &&
                    value !== '<p><br></p>' &&
                    value !== '<p></p>' &&
                    value &&
                    classes(pt8, field.type !== FIELDS.RICH_TEXT && pb4),
                  style({
                    paddingRight: 4,
                    paddingLeft: 4,
                    ...media({ type: 'print' }, { display: 'none' }),
                  }),
                )}
                alignRight
                baseline="none"
              >
                {isEditable && renderButton()}
              </Box>
              {field.type === FIELDS.REPEATER && (
                <Box
                  className={classes(
                    style({
                      bottom: 0,
                      paddingTop: 4,
                      paddingBottom: 4,
                      paddingRight: 4,
                      borderRadius: '0 0 4px 4px !important',
                    }),
                  )}
                  flex
                  baseline="background"
                  sticky
                  alignRight
                >
                  <Button
                    baseline={disabled ? 'disabled' : 'primary'}
                    label={renderEditLabel()}
                    disabled={field.disabled}
                    onClick={onEdit({ field, value, dialogType })}
                    size="xs"
                    disableAnimation
                    square
                    center
                  />
                </Box>
              )}
            </Box>
          </Box>
        )
      }}
    </OperatorWrapper>
  )
}

export const renderView = (props: {
  fields: any
  parentName: any
  flatten: any
  value: any
  form?: any
  isEditable?: boolean
  pathName?: string
  hiddenFields?: any
  highlightedFields?: any
  onUpdate?: any
  onHide?: any
  onShow?: any
  onHighlight?: any
  readable?: boolean
  parentRowId?: string
  /**
   * Returns table row
   */
  alt?: boolean
  local?: LOCAL_TYPE
  labelFilter?: string
  onLabelFilter?: any
  translate?: boolean
}) => {
  const {
    fields,
    parentName,
    flatten,
    value,
    form,
    isEditable,
    pathName,
    onUpdate,
    onHide,
    onHighlight,
    alt,
    readable = true,
    local = 'en',
    parentRowId,
    labelFilter,
    onLabelFilter,
  } = props

  const onEdit = (props: { field: any; value: any; dialogType?: string }) => () => {
    const { field, value } = props
    field.hidden = false

    switch (field.type) {
      case FIELDS.PASSWORD_CHANGE: {
        Modal.push({
          type: 'PasswordChange',
          title: GLOBAL_CONTENTS.changePassword[local],
          componentProps: {
            field,
            pathName,
            value,
            onUpdate,
            self: false,
          },
        })
        break
      }
      case FIELDS.REPEATER: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          containerClassName: style({
            minWidth: 640,
            maxWidth: 640,
            ...createCustomMediaQuery({
              maxWidth: 1044,
              style: {
                minWidth: '100%',
                maxWidth: '100%',
              },
            }),
          }),
          componentProps: {
            field,
            fields: field.fields,
            replace: field.name.replace(new RegExp('^body.'), ''),
            parentName,
            pathName,
            value,
            onUpdate,
            onHide,
            parentRowId,
          },
        })
        break
      }
      case FIELDS.TABLE: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          containerClassName: style({
            minWidth: 960,
            maxWidth: 960,
            ...createCustomMediaQuery({
              maxWidth: 1044,
              style: {
                minWidth: '880px',
                maxWidth: '880px',
              },
            }),
          }),
          componentProps: {
            field,
            parentName,
            replace: field.name.replace(new RegExp('^body.'), ''),
            pathName,
            value,
            onUpdate,
            onHide,
          },
        })
        break
      }
      case FIELDS.ASYNC_SELECT:
      case FIELDS.POPULATE_SELECT: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          componentProps: {
            field,
            parentName,
            pathName,
            value,
            onUpdate,
            onHide,
            parentRowId,
          },
        })
        break
      }
      case FIELDS.ASYNC_MULTI_SELECT:
      case FIELDS.POPULATE_MULTI_SELECT: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          containerClassName: style({
            minWidth: 400,
            maxWidth: 400,
            ...createCustomMediaQuery({
              maxWidth: 1044,
              style: {
                minWidth: '100%',
                maxWidth: '100%',
              },
            }),
          }),
          componentProps: {
            field,
            replace: field.name.replace(new RegExp('^body.'), ''),
            parentName,
            pathName,
            value,
            onUpdate,
            onHide,
          },
        })
        break
      }
      case FIELDS.HOURS: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          componentProps: {
            field,
            replace: field.name.replace(new RegExp('^body.'), ''),
            parentName,
            pathName,
            value,
            onUpdate,
            onHide,
          },
        })
        break
      }
      case FIELDS.RICH_TEXT: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          componentProps: {
            field,
            parentName,
            pathName,
            value,
            onUpdate,
            onHide,
            maxWidth: 800,
          },
        })
        break
      }

      case FIELDS.UPLOAD: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          componentProps: {
            field,
            parentName,
            pathName,
            replace: field.name.replace(new RegExp('^body.'), ''),
            value,
            onHide,
            onUpdate,
          },
        })
        break
      }

      default: {
        Modal.push({
          type: 'FieldEdit',
          title: GLOBAL_CONTENTS.editField[local],
          componentProps: {
            field,
            parentName,
            pathName,
            replace: field.name.replace(new RegExp('^body.'), ''),
            value,
            onHide,
            onUpdate,
          },
        })
        break
      }
    }
  }

  return fields.map((field: any, key: any) => {
    if (field?.isAdminOnly && !window.location.pathname.includes('/admin/')) {
      return
    }

    switch (field?.type) {
      case FIELDS.REPEATER: {
        const fieldName = parentName ? `${parentName}.${field.name}` : field.name
        const fieldValue = get(fieldName, value, 'array')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          readable,
          local,
          value: structureArray(fieldValue),
          component: (
            <Box row fullWidth baseline="none">
              {fieldValue && fieldValue.length > 0 ? (
                fieldValue.map((item: any, key: any) => {
                  return (
                    <Box
                      key={key}
                      baseline={'background'}
                      className={classes(pr4, pt4, pb4, pl8)}
                      fullWidth
                      row
                    >
                      <Box className={classes(w100, p16)} rounded border>
                        <Text size="sm" className={classes(w100)}>
                          {renderView({
                            fields: field.fields,
                            parentName: `${parentName ? `${parentName}.` : ''}${field.name}.${key}`,
                            flatten: flatten,
                            value,
                            form,
                            isEditable: false,
                            pathName,
                            onUpdate,
                            onHide,
                            onHighlight,
                            parentRowId,
                            readable,
                            local,
                            hiddenFields: props.hiddenFields,
                            highlightedFields: props.highlightedFields,
                            labelFilter,
                            onLabelFilter,
                          })}
                        </Text>
                      </Box>
                    </Box>
                  )
                })
              ) : (
                <Box className={pl12} baseline="none">
                  <Text size="sm">-</Text>
                </Box>
              )}
            </Box>
          ),
          isEditable: false,
          disablePadding: true,
          flatten,
          onEdit,
          alt,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldValue],
          pathName,
          key,
        })
      }

      case FIELDS.TABLE: {
        let fieldValue: any

        if (parentName !== undefined && value[parentName]) {
          fieldValue = value[parentName][field.name]
        } else if (parentName != undefined && value[parentName.replace('body.', '')]) {
          fieldValue = value[parentName.replace('body.', '')][field.name]
        } else if (value.body && value.body[field.name.replace('body.', '')]) {
          if (parentName != undefined && parentName.includes('body.')) {
            fieldValue = value.body[parentName.replace('body.', '')][field.name]
          } else {
            fieldValue = value.body[field.name.replace('body.', '')]
          }
        }

        return renderViewItem({
          field: {
            ...field,
            name: `${parentName ? `${parentName}.` : ''}${field.name}`,
          },
          local,
          readable,
          value: structureArray(fieldValue),
          isEditable: true,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldValue],
          pathName,
          alt,
          key,
        })
      }
      case FIELDS.GROUP: {
        const fieldName = `${
          parentName ? `${parentName}${field.name ? '.' : ''}` : ''
        }${field.name || ''}`

        // if (
        //   field.fields
        //     .map((a: any) => `${fieldName}.${a.name}`)
        //     .filter((a: any) => props.hiddenFields[a]).length === field.fields.length
        // ) {
        //   return <></>
        // }

        field.fields = field.fields.map((a: any) => {
          return {
            ...a,
            parentField: field,
          }
        })

        const hiddenFieldsPerGroup = (() => {
          const parsed: any = {}
          for (const key in props.hiddenFields) {
            if (props.hiddenFields[key].parentLabel === field.label) {
              parsed[key] = props.hiddenFields[key]
            }
          }
          return parsed
        })()

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          local,
          readable,
          component: (
            <>
              {renderView({
                fields: field.fields,
                parentName: fieldName,
                flatten: flatten,
                value,
                form,
                isEditable,
                readable,
                pathName,
                onUpdate,
                onHide,
                onHighlight,
                parentRowId,
                hiddenFields: props.hiddenFields,
                highlightedFields: props.highlightedFields,
                labelFilter,
                onLabelFilter,
              })}
              {Object.keys(hiddenFieldsPerGroup).length > 0 && (
                <HiddenFields hiddenFields={hiddenFieldsPerGroup} onShow={props.onShow} />
              )}
            </>
          ),
          isEditable: false,
          key,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.ASYNC_SELECT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const parsedValue = flatten[`${fieldName}.${field.labelKey}`]

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }
        const value = {
          [field.labelKey]: parsedValue,
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          local,
          readable,
          value,
          parsedValue,
          key,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.POPULATE_SELECT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        const targetDocument = get(fieldName, value)
        const flattenTargetDocument = flat(targetDocument)
        const parsedValue = flattenTargetDocument[`${field.labelKey}`]

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          local,
          readable,
          value: targetDocument,
          parsedValue,
          key,
          isEditable,
          flatten: {},
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.ASYNC_CHECKBOX: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')
        const parsedValue = '-'
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        const Component = withRouter((props: any) => {
          const { match } = props

          const [entries, $entries] = useFetch<{
            data: any[]
          }>(
            {
              data: [],
            },
            {
              updateGetParams: false,
              url: `${process.env.REACT_APP_API_URL}/${field.apiUrl
                .replace('{{orgId}}', (match as any).params.orgId)
                .replace('{{parentId}}', (match as any).params.parentRowId)}`,
            },
          )

          useEffect(() => {
            if (entries.mounted) {
              $entries.get({
                search: {
                  limit: 200,
                  sort: {
                    key: [field.labelKey],
                    direction: 1,
                  },
                  ...(() => {
                    if (field.populateItems) {
                      return {
                        populate: field.populateItems,
                      }
                    }
                  })(),
                },
              })
            }
          }, [entries.mounted])

          const value: any = []

          for (const key in fieldValue) {
            if (fieldValue[key]) {
              const found = entries.payload.data?.filter((a: any) => {
                return a.id === key
              })[0]

              if (found) {
                value.push(flat(found)[field.labelKey])
              }
            }
          }

          return <Text size="sm">{value.length ? value.sort().join(', ') : '-'}</Text>
        })

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          readable,
          value: fieldValue,
          parsedValue,
          component: <Component />,
          isEditable,
          key,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.POPULATE_MULTI_SELECT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue: any = get(fieldName, value, 'array')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          component: (
            <Box row fullWidth baseline="none">
              {fieldValue && fieldValue.length > 0
                ? fieldValue?.map?.((item: any, key: any) => {
                    const label = get(field.labelKey, item, 'string')

                    return (
                      <Text
                        size="sm"
                        className={classes(w100)}
                        key={key}
                        readable
                        baseline="surface"
                      >
                        {label}
                      </Text>
                    )
                  })
                : '-'}
            </Box>
          ),
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.ASYNC_MULTI_SELECT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue: any = get(fieldName, value, 'array')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        const structuredValue = structureArray(fieldValue)

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: structuredValue,
          key,
          readable,
          isEditable,
          component: (
            <Box row fullWidth baseline="none">
              {fieldValue && fieldValue.length > 0
                ? fieldValue.map((item: any, key: any) => {
                    const label = get(field.labelKey, item.id, 'string')

                    return (
                      <Text
                        size="sm"
                        className={classes(w100)}
                        key={key}
                        readable
                        baseline="surface"
                      >
                        {label}
                      </Text>
                    )
                  })
                : '-'}
            </Box>
          ),
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.BREAK: {
        return (
          <Box
            key={key}
            className={classes(
              mt8,
              mb16,
              style({
                pageBreakInside: 'avoid',
              }),
            )}
            row
          >
            <Line dark />
          </Box>
        )
      }
      case FIELDS.CHECKBOX: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = flatten[fieldName]
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          parsedValue: renderCheckboxValue(fieldValue),
          isEditable,
          readable,
          key,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
        })
      }
      case FIELDS.MONEY:
        const fieldName = `${parentName !== undefined ? `${parentName}.` : ''}${field.name}`
        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return (
          <Box key={key} row className={mb16}>
            <Text size="xs" semiBold>
              {field.label}
            </Text>
            <Text size="sm" className={mb8}>
              {flatten[fieldName] ? formatPrice(flatten[fieldName]) : '-'}
            </Text>
            <Line />
          </Box>
        )
      case FIELDS.DATE: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const tempDate = new Date(flatten[fieldName])
        const parsedValue = flatten[fieldName]
          ? moment(tempDate)
              .utc()
              .locale(process.env.REACT_APP_LANGUAGE || 'en')
              .format(process.env.REACT_APP_LANGUAGE ? 'yyyy-MM-DDD' : 'MMM DD, yyyy')
          : '-'
        const fieldValue = formatDate(new Date(flatten[fieldName]))
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          readable,
          value: fieldValue,
          parsedValue,
          key,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.HOURS: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')

        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }
        let parsedValue = '-'

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        if (fieldValue.closed) {
          parsedValue = 'Closed'
        } else if (fieldValue.open && fieldValue.close) {
          parsedValue = `${fieldValue.open} - ${fieldValue.close}`
        } else if (fieldValue.open) {
          parsedValue = `${fieldValue.open}`
        } else if (fieldValue.close) {
          parsedValue = `${fieldValue.close}`
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          readable,
          value: fieldValue,
          parsedValue,
          key,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.ASYNC_SELECT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }
        const parsedValue = '-'

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        const Component = withRouter((props: any) => {
          const { match } = props

          const [entries, $entries] = useFetch<{
            data: any[]
          }>(
            {
              data: [],
            },
            {
              updateGetParams: false,
              url: `${process.env.REACT_APP_API_URL}/${field.apiUrl
                .replace('{{orgId}}', (match as any).params.orgId)
                .replace('{{parentId}}', (match as any).params.parentRowId)}`,
            },
          )

          useEffect(() => {
            if (entries.mounted) {
              $entries.get({
                search: {
                  limit: 200,
                  sort: {
                    key: [field.labelKey],
                    direction: 1,
                  },
                  ...(() => {
                    if (field.lookups) {
                      return {
                        populate: field.lookups,
                      }
                    }
                  })(),
                },
              })
            }
          }, [entries.mounted])

          const value: any = []

          for (const key in fieldValue) {
            if (fieldValue[key]) {
              const found = entries.payload.data?.filter((a: any) => {
                return a.id === key
              })[0]

              if (found) {
                value.push(flat(found)[field.labelKey])
              }
            }
          }

          return (
            <Text
              size="sm"
              className={style({
                // display: 'block !important',
                // width: '100%',
                // clear: 'both',
                pageBreakInside: 'avoid',
              })}
            >
              {value.length ? value.sort().join(', ') : '-'}
            </Text>
          )
        })

        return renderViewItem({
          field: {
            ...field,
            name: `${parentName ? `${parentName}.` : ''}${field.name}`,
          },
          comments,
          local,
          value: fieldValue,
          parsedValue,
          readable,
          component: <Component />,
          isEditable,
          key,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.PASSWORD_CHANGE: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          local,
          readable,
          value: '********',
          key,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.RICH_TEXT: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = flatten[fieldName] || '-'
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          disablePadding: true, // (fieldValue !== '<p><br></p>' && fieldValue !== '<p></p>' &&),
          component: (
            <Box baseline="none" className={pl4}>
              <RichTextView
                __html={fieldValue === '<p><br></p>' || fieldValue === '<p></p>' ? '-' : fieldValue}
              />
            </Box>
          ),
          flatten,
          onEdit,
          onHide,
          onUpdate,
          pathName,
          highlighted: props?.highlightedFields?.[fieldName],
          alt,
        })
      }
      case FIELDS.TEXTAREA: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = flatten[fieldName] || '-'
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          disablePadding: false,
          component: (
            <Text size="sm" readable preLine baseline="surface">
              {fieldValue}
            </Text>
          ),
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.DASHBOARD_PERMISSIONS: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          component: <DashboardPermissionFieldView value={fieldValue} {...field} />,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.PERMISSIONS: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          component: <PermissionsFieldView value={fieldValue} {...field} />,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.PERMISSION: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'object')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          component: <PermissionFieldView value={fieldValue} {...field} />,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
        })
      }
      case FIELDS.UPLOAD: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const fieldValue = get(fieldName, value, 'array')
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          key,
          readable,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
          component: <UploadFieldView value={fieldValue} />,
        })
      }
      case FIELDS.MULTI_CHECKBOX: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field.name}`
        const rawValue = get(fieldName, value, 'object')
        const parsedValue = Object.keys(rawValue).length ? Object.keys(rawValue) : '-'

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          value: rawValue,
          parsedValue: parsedValue,
          component: (
            <Box row fullWidth baseline="none">
              {Object.keys(rawValue).length > 0
                ? Object.keys(rawValue).map((item: any, key: any) => {
                    if (rawValue[item]) {
                      return (
                        <Text
                          size="sm"
                          className={classes(w100)}
                          key={key}
                          readable
                          baseline="surface"
                        >
                          {item}
                        </Text>
                      )
                    }
                  })
                : '-'}
            </Box>
          ),
          isEditable,
          readable,
          key,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          pathName,
        })
      }

      case FIELDS.NUMBER: {
        const fieldName = `${parentName ? `${parentName}.` : ''}${field?.name}`
        const fieldValue = flatten[fieldName] || '-'
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        return renderViewItem({
          field: {
            ...field,
            name: fieldName,
          },
          comments,
          local,
          value: fieldValue,
          parsedValue: fieldValue,
          key,
          readable,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          pathName,
          highlighted: props?.highlightedFields?.[fieldName],
          alt,
          component: (
            <>
              <Text>{fieldValue && fieldValue !== '-' ? fieldValue.toLocaleString() : '-'}</Text>
            </>
          ),
        })
      }

      case FIELDS.CUSTOM_FIELD: {
        const Field = FieldObject?.[field.customType]?.view
        const Action = FieldObject?.[field.customType]?.action
        const fieldName = `${parentName ? `${parentName}.` : ''}${field?.name}`
        const fieldValue = get(fieldName, value)
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []

        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
          onLabelFilter?.(false)
        }

        if (Field) {
          return renderViewItem({
            field: {
              ...field,
              name: fieldName,
            },
            comments,
            local,
            value: fieldValue,
            key,
            readable,
            isEditable,
            flatten,
            onEdit,
            onUpdate,
            onHide,
            onHighlight,
            highlighted: props?.highlightedFields?.[fieldName],
            pathName,
            alt,
            component: <Field value={fieldValue} field={field} />,
            action: Action ? (
              <Action
                value={fieldValue}
                form={value}
                name={field?.name}
                fullName={fieldName}
                onUpdate={onUpdate}
                pathName={pathName}
              />
            ) : (
              <Fragment />
            ),
          })
        }
      }

      default: {
        const fieldName = field.nameHard || `${parentName ? `${parentName}.` : ''}${field?.name}`
        const fieldValue = flatten[fieldName] || get(fieldName, value, undefined, undefined) || '-'
        const parsedValue =
          field.translate && config.dictionary?.[fieldValue]?.[local]
            ? config.dictionary?.[fieldValue]?.[local]
            : fieldValue
        const comments =
          flatten[`${fieldName}Comments`] || get(`${fieldName}Comments`, value, 'array') || []
        if (props.hiddenFields && props.hiddenFields[fieldName]) {
          return <></>
        }

        if (labelFilter && !field.label.toLowerCase().includes(labelFilter.toLowerCase())) {
          onLabelFilter?.(true)
          return <></>
        } else {
        }

        return renderViewItem({
          field: {
            ...field,
            name: field.nameHard || fieldName,
          },
          comments,
          local,
          value: fieldValue !== '-' ? fieldValue : undefined,
          key,
          readable,
          isEditable,
          flatten,
          onEdit,
          onUpdate,
          onHide,
          onHighlight,
          highlighted: props?.highlightedFields?.[fieldName],
          pathName,
          alt,
          parsedValue,
        })
      }
    }
  })
}

const renderCheckboxValue = (item: any) => {
  return item ? 'Yes' : 'No'
}
